import React, { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Settings, UserPlus, ChevronLeft, Clock, Bell, Sparkles } from "lucide-react";
import {
  useNotifications,
  useMarkNotificationsRead,
} from "../../hooks/queries/useNotifications";
import { savedSearchAlertPath } from "../../utils/notificationLinks";

// Map notification type to icon + colors
const TYPE_STYLES = {
  system: {
    icon: Settings,
    iconBg: "bg-blue-400",
    borderColor: "border-blue-300",
    titleColor: "text-blue-700",
  },
  talent: {
    icon: UserPlus,
    iconBg: "bg-green-400",
    borderColor: "border-green-300",
    titleColor: "text-green-700",
  },
  // Aviso semanal de talento nuevo en una búsqueda guardada (DEV-74).
  saved_search_alert: {
    icon: Sparkles,
    iconBg: "bg-indigo-500",
    borderColor: "border-indigo-300",
    titleColor: "text-indigo-800",
  },
  progress: {
    icon: Bell,
    iconBg: "bg-purple-400",
    borderColor: "border-purple-300",
    titleColor: "text-purple-700",
  },
  default: {
    icon: Bell,
    iconBg: "bg-gray-400",
    borderColor: "border-gray-300",
    titleColor: "text-gray-700",
  },
};

const formatTime = (iso) => {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return `${Math.floor(diff / 86400)}d`;
};

const NotificationsPanel = ({ isOpen, onClose, userId, onNotificationClick }) => {
  const [activeFilter, setActiveFilter] = useState("HOY");

  const notificationsQuery = useNotifications({
    enabled: isOpen && Boolean(userId),
  });
  const { mutate: markNotificationsRead } = useMarkNotificationsRead();

  const notifications = useMemo(
    () => notificationsQuery.data || [],
    [notificationsQuery.data],
  );
  const loading = notificationsQuery.isLoading;

  // Mark unread as read once they are shown.
  useEffect(() => {
    if (!isOpen || !userId) return;
    const unreadIds = notifications.filter((n) => !n.read).map((n) => n.id);
    if (unreadIds.length > 0) {
      markNotificationsRead({ ids: unreadIds });
    }
  }, [isOpen, userId, notifications, markNotificationsRead]);

  // Filter: HOY = today, SEMANA = last 7 days, ANTES = older
  const filteredNotifications = notifications.filter((n) => {
    if (!n.created_at) return true;
    const diff = (Date.now() - new Date(n.created_at).getTime()) / 1000;
    if (activeFilter === "HOY") return diff < 86400;
    if (activeFilter === "SEMANA") return diff < 86400 * 7;
    return diff >= 86400 * 7;
  });

  const filters = ["HOY", "SEMANA", "ANTES"];

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black bg-opacity-30 z-40"
          />

          {/* Panel */}
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.3 }}
            className="fixed right-0 top-16 h-[calc(100%-4rem)] w-full bg-[#C7D0E5] z-50 shadow-2xl flex flex-col sm:w-96 lg:top-0 lg:h-full"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-300">
              <h2 className="text-2xl font-bold text-gray-900">
                Notificaciones
              </h2>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
              >
                <X size={20} className="text-gray-700" />
              </button>
            </div>

            {/* Filter Tabs */}
            <div className="flex items-center gap-4 px-6 pt-4 pb-4 border-b border-gray-300">
              {filters.map((filter) => (
                <button
                  key={filter}
                  onClick={() => setActiveFilter(filter)}
                  className={`px-4 py-2 rounded-lg font-bold text-sm transition-colors ${
                    activeFilter === filter
                      ? "bg-[#3F51B5] text-white"
                      : "bg-white text-gray-900 hover:bg-gray-100"
                  }`}
                >
                  {filter}
                </button>
              ))}
            </div>

            {/* Notifications List */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : filteredNotifications.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <Bell size={40} className="text-gray-400 mb-3" />
                  <p className="text-gray-500 font-medium">
                    Sin notificaciones
                  </p>
                  <p className="text-gray-400 text-sm mt-1">
                    No hay notificaciones en este período.
                  </p>
                </div>
              ) : (
                filteredNotifications.map((notification) => {
                  const style =
                    TYPE_STYLES[notification.type] || TYPE_STYLES.default;
                  const IconComponent = style.icon;
                  const isClickable =
                    typeof onNotificationClick === "function" &&
                    Boolean(
                      notification?.metadata?.contact_request_id ||
                        notification?.metadata?.academy_athlete_id ||
                        savedSearchAlertPath(notification),
                    );
                  return (
                    <motion.div
                      key={notification.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      onClick={
                        isClickable
                          ? () => onNotificationClick(notification)
                          : undefined
                      }
                      onKeyDown={
                        isClickable
                          ? (event) => {
                              if (event.key === "Enter" || event.key === " ") {
                                event.preventDefault();
                                onNotificationClick(notification);
                              }
                            }
                          : undefined
                      }
                      role={isClickable ? "button" : undefined}
                      tabIndex={isClickable ? 0 : undefined}
                      className={`bg-white rounded-lg border ${style.borderColor} p-4 shadow-sm hover:shadow-md transition-shadow ${
                        !notification.read ? "ring-2 ring-blue-300" : ""
                      } ${isClickable ? "cursor-pointer" : ""}`}
                    >
                      <div className="flex items-start gap-4">
                        <div
                          className={`${style.iconBg} p-2 rounded-lg flex-shrink-0`}
                        >
                          <IconComponent size={20} className="text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2 mb-1">
                            <h3
                              className={`font-bold text-base ${style.titleColor}`}
                            >
                              {notification.title || "Notificación"}
                            </h3>
                            <span className="text-xs text-gray-400 flex-shrink-0 flex items-center gap-1">
                              <Clock size={12} />
                              {formatTime(notification.created_at)}
                            </span>
                          </div>
                          <p className="text-sm text-gray-700 font-normal">
                            {notification.message ||
                              notification.description ||
                              ""}
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  );
                })
              )}
            </div>

            {/* Footer with hide button */}
            <div className="p-4 border-t border-gray-300">
              <button
                onClick={onClose}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-gray-700 font-medium"
              >
                <ChevronLeft size={18} />
                <span>Ocultar</span>
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default NotificationsPanel;
